/**
 * Policy Kernel SoT — Settings AI. GET configured + effective; PATCH CAS.
 * Soft≠ invent overlay chips as the live mode. Env flags are read-only here.
 */
import { useEffect, useState } from "react";
import { html, Card, Field, Alert, KV } from "./ui.js";
import { api, useAction } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import { autonomyPlanesOf } from "./autonomy-planes.js";

const MODES = ["AUTO", "MONITOR_NOTIFY", "PAUSED"];

export function PolicyKernelPanel({ toast }) {
  const { run, busy, envelope } = useAction();
  const [configured, setConfigured] = useState(null);
  const [effective, setEffective] = useState(null);
  const [mode, setMode] = useState("");
  const [reason, setReason] = useState("operator_policy");
  const [loadErr, setLoadErr] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    setLoadErr("");
    try {
      const cfg = await api("/ai/autonomy/policy", { method: "GET" });
      const eff = await api("/ai/autonomy/policy/effective", { method: "GET" });
      setConfigured(cfg && typeof cfg === "object" ? cfg : null);
      setEffective(eff && typeof eff === "object" ? eff : null);
      const m = String((cfg && cfg.configured_mode) || (cfg && cfg.policy && cfg.policy.execution_mode) || "");
      if (m) setMode(m);
    } catch (e) {
      setLoadErr(String((e && e.message) || e || "policy_load_failed"));
      setConfigured(null);
      setEffective(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const rev = Number(
    (configured && configured.policy_rev)
    || (configured && configured.policy && configured.policy.policy_rev)
    || 0,
  );
  const canWrite = configured && configured.ok === true && Number.isFinite(rev);

  const save = async () => {
    if (!canWrite) {
      toast && toast("Policy Kernel not loaded — not saved", "warn");
      return;
    }
    if (!MODES.includes(mode)) {
      toast && toast("Unknown mode refused — not coerced to AUTO", "warn");
      return;
    }
    const out = await run("/ai/autonomy/policy", {
      method: "PATCH",
      body: {
        execution_mode: mode,
        expected_rev: rev,
        reason_code: String(reason || "operator_policy").slice(0, 64),
      },
    });
    if (!out || out.ok === false) {
      toast && toast("Policy save refused — CAS or seat", "warn");
      return;
    }
    toast && toast("Policy Kernel saved · rev " + String(out.policy_rev || ""), "go");
    await load();
  };

  const cfgMode = String((configured && configured.configured_mode) || "—");
  const effMode = String((effective && effective.effective_mode) || "—");
  const mismatch = cfgMode !== "—" && effMode !== "—" && cfgMode !== effMode;
  const planes = autonomyPlanesOf(effective);
  const flags = (effective && effective.class_c_flags && typeof effective.class_c_flags === "object")
    ? effective.class_c_flags
    : null;
  const flagOn = (k) => flags && flags[k] === true ? "on" : (flags && flags[k] === false ? "off" : "—");

  return html`
    <${Card} title="Policy Kernel" lede="Configured vs Effective. This is the fence — not a playbook picker."
        data-cmc-policy-kernel="1">
      ${loadErr ? html`<${Alert} tone="bad" title="Policy Kernel unavailable" say=${loadErr}/>` : null}
      ${envelope
        ? html`<${KV} k="Last action" v=${envelopeSummary(envelope) || "—"} tone=${envelope.ok ? "hi" : "warn"}/>`
        : null}
      <div className="btn-row" style=${{ marginBottom: 8 }}>
        <button type="button" className="btn btn--ghost btn--sm" disabled=${loading || busy}
          onClick=${() => load()}>${loading ? "Loading…" : "Refresh configured / effective"}</button>
      </div>
      <${KV} k="Configured" v=${cfgMode} tone=${mismatch ? "warn" : "hi"}/>
      <${KV} k="Effective" v=${effMode} tone=${mismatch ? "warn" : "hi"}/>
      <${KV} k="Health" v=${planes.runtime_health || "—"} tone=${planes.fault ? "warn" : "hi"}/>
      <${KV} k="Ledger" v=${planes.ledger || "—"}/>
      <${KV} k="Manual only" v=${planes.manual_only ? "yes" : "no"}/>
      ${planes.fault
        ? html`<${Alert} tone="bad" title="Autonomy fault"
            say=${planes.reason === "configured_auto_run_missing"
              ? "AUTO is configured but no Mission Run — system fault, never leftover-approve."
              : (planes.reason || "Autonomy fault")}/>`
        : null}
      <div data-cmc-autonomy-planes="1" data-health=${planes.runtime_health || ""}
          data-ledger=${planes.ledger || ""} data-fault=${planes.fault ? "1" : "0"}></div>
      <${KV} k="Revision" v=${String(rev)}/>
      <${KV} k="Enforcement" v=${String((effective && effective.enforcement) || (configured && configured.enforcement) || "—")}/>
      <div data-cmc-s1-class-c="1">
        <${KV} k="Require human approval" v=${flagOn("AI_DECISION_REQUIRE_HUMAN_APPROVAL")}/>
        <${KV} k="Host model sealed" v=${flagOn("AI_HOST_MODEL_SEALED")}/>
        <${KV} k="Live dispatch" v=${flagOn("AI_AUTONOMY_LIVE_DISPATCH_ENABLED")}/>
        <${KV} k="Live camera" v=${flagOn("AI_AUTONOMY_LIVE_CAMERA_ENABLED")}/>
        <${KV} k="Flags writable here" v=${flags && flags.writable_from_cmc === true ? "yes" : "no"}/>
      </div>
      ${mismatch ? html`<${Alert} tone="warn" title="Configured ≠ Effective"
        say="A tighter scope (site / incident Hold / E-Stop) is winning. Policy Kernel did not invent AUTO."/>` : null}
      <${Field} label="Execution mode">
        <div className="chips" style=${{ paddingTop: 2 }}>
          ${MODES.map((m) => html`
            <button type="button" key=${m} className="chip" data-tone="ai" data-sel=${mode === m ? "1" : "0"}
              disabled=${busy || !canWrite}
              onClick=${() => setMode(m)}>${m}</button>`)}
        </div>
      </${Field}>
      <${Field} label="Reason code">
        <input className="input" value=${reason} onInput=${(e) => setReason(e.target.value)}
          placeholder="operator_policy"/>
      </${Field}>
      <button type="button" className="btn btn--go btn--sm" disabled=${busy || !canWrite} onClick=${save}
        style=${{ marginTop: 8 }}>Save Policy Kernel</button>
      <p className="note">Unknown modes are refused. Overlay chips below are compatibility — they do not replace this CAS.</p>
    </${Card}>
  `;
}

export default PolicyKernelPanel;
